import { WORLDS, type WorldSlug } from '@/lib/worlds';
import type { PlaybackRate } from '@/stores/audioStore';

/**
 * Shared helpers for the 3 audio players (Mini-Player, Inline-Player,
 * Now-Playing). Extracted from StoryInlineAudioPlayer in Batch 6.2.3
 * to avoid duplication across player variants.
 */

export const PLAYBACK_RATES: readonly PlaybackRate[] = [0.75, 1, 1.25, 1.5] as const;

export const WORLD_NAME_BY_SLUG: Record<WorldSlug, string> = {
  aa: WORLDS.AA.name,
  sb: WORLDS.SB.name,
  kp: WORLDS.KP.name,
};

/**
 * Format a seconds value as `m:ss` for display in the scrub-bar timestamp
 * row. Negative values clamp to 0; fractional seconds floor. Minutes
 * range is unbounded (`65:00` renders fine).
 */
export function formatTime(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

/**
 * Format a PlaybackRate for the Speed-toggle label using German decimal
 * separator (`1,0×` not `1.0×`).
 */
export function formatRate(rate: PlaybackRate): string {
  return rate === 1 ? '1,0' : rate.toString().replace('.', ',');
}

/**
 * Cycle to the next rate in PLAYBACK_RATES order: 0,75 → 1,0 → 1,25 →
 * 1,5 → 0,75. Used by the Speed-toggle button.
 */
export function nextRate(current: PlaybackRate): PlaybackRate {
  const idx = PLAYBACK_RATES.indexOf(current);
  const nextIdx = idx === -1 ? 0 : (idx + 1) % PLAYBACK_RATES.length;
  return PLAYBACK_RATES[nextIdx] ?? 1;
}

/**
 * Format an ISO-8601 timestamp as `DD.MM.YYYY`. Empty string on invalid
 * input — callers render nothing for that case (graceful degradation).
 * Uses local timezone for the date boundary; story.createdAt in practice
 * never straddles a boundary in a meaningful way at German locales.
 */
export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${d.getFullYear()}`;
}
